import { useEffect } from "react";

// Touch gestures for the mobile nav drawer:
//   - closed: a horizontal swipe STARTING in the inward left-edge band opens it
//   - open:   a horizontal swipe to the left (from anywhere) closes it
// Threshold-based (snaps via the existing CSS transition-transform), not
// finger-following — simple and good enough for an off-canvas nav.
//
// Desktop is excluded (the sidebar is static there, no drawer). Vertical drags
// bail so the drawer's own scroll and page scroll are untouched. Listeners are
// passive — we only DETECT the gesture, never block native scrolling.
//
// The OPEN swipe must START in an inward band, NOT at the very edge: iOS Safari
// reserves the leftmost ~20px for its back-navigation gesture, so starting there
// triggered "back" instead of opening. The band begins past that reserved strip
// (so it no longer fights the browser) and ends close enough that it still reads
// as an edge-origin swipe rather than a mid-content drag. The CLOSE swipe (when
// open) can start anywhere and is unaffected.
const OPEN_ZONE_MIN_PX = 36; // clear of iOS Safari's left-edge back-swipe strip
const OPEN_ZONE_MAX_PX = 100; // still near enough to read as an edge swipe
const COMMIT_PX = 56; // horizontal travel that commits the gesture
const VERTICAL_SLOP_PX = 24; // vertical drift past which we treat it as a scroll
const DESKTOP_MIN_PX = 1024; // lg breakpoint — gestures only below it

export function useEdgeSwipeDrawer(open: boolean, setOpen: (open: boolean) => void) {
  useEffect(() => {
    let startX = 0;
    let startY = 0;
    let tracking = false;

    const onStart = (e: TouchEvent) => {
      if (window.innerWidth >= DESKTOP_MIN_PX) return;
      const t = e.touches[0];
      if (!t) return;
      // When closed, only a touch starting in the inward edge band arms an open
      // swipe (skipping the iOS back-swipe strip). When open, a swipe starting
      // anywhere can close it.
      if (!open && (t.clientX < OPEN_ZONE_MIN_PX || t.clientX > OPEN_ZONE_MAX_PX)) {
        tracking = false;
        return;
      }
      startX = t.clientX;
      startY = t.clientY;
      tracking = true;
    };

    const onMove = (e: TouchEvent) => {
      if (!tracking) return;
      const t = e.touches[0];
      if (!t) return;
      const dx = t.clientX - startX;
      const dy = t.clientY - startY;
      // Mostly-vertical drag → it's a scroll, not a drawer gesture. Stop tracking.
      if (Math.abs(dy) > VERTICAL_SLOP_PX && Math.abs(dy) > Math.abs(dx)) {
        tracking = false;
        return;
      }
      if (!open && dx > COMMIT_PX) {
        setOpen(true);
        tracking = false;
      } else if (open && dx < -COMMIT_PX) {
        setOpen(false);
        tracking = false;
      }
    };

    const onEnd = () => {
      tracking = false;
    };

    document.addEventListener("touchstart", onStart, { passive: true });
    document.addEventListener("touchmove", onMove, { passive: true });
    document.addEventListener("touchend", onEnd, { passive: true });
    document.addEventListener("touchcancel", onEnd, { passive: true });
    return () => {
      document.removeEventListener("touchstart", onStart);
      document.removeEventListener("touchmove", onMove);
      document.removeEventListener("touchend", onEnd);
      document.removeEventListener("touchcancel", onEnd);
    };
  }, [open, setOpen]);
}
